import { useState, useCallback, useEffect } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * Custom hook to manage drawer state and behavior
 * Handles both mobile and desktop drawer states
 * Drawer state is only controlled by the toggle button in the header
 * Uses localStorage to persist drawer state across navigation
 * 
 * @returns {Object} Drawer state and handlers
 */
const useDrawer = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  
  // Initialize state from localStorage if available, otherwise use defaults
  // Mobile drawer state (open/closed)
  const [mobileOpen, setMobileOpen] = useState(() => {
    const savedState = localStorage.getItem('mobileDrawerOpen');
    return savedState !== null ? JSON.parse(savedState) : false;
  });
  
  // Desktop drawer state (mini/expanded)
  const [miniDrawer, setMiniDrawer] = useState(() => {
    const savedState = localStorage.getItem('miniDrawerState');
    return savedState !== null ? JSON.parse(savedState) : true;
  });

  // Persist drawer state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('mobileDrawerOpen', JSON.stringify(mobileOpen));
  }, [mobileOpen]);

  useEffect(() => {
    localStorage.setItem('miniDrawerState', JSON.stringify(miniDrawer));
  }, [miniDrawer]);

  // Open drawer handler - expands the drawer
  const handleDrawerOpen = useCallback(() => {
    if (isDesktop) {
      setMiniDrawer(false);
    } else {
      setMobileOpen(true);
    }
  }, [isDesktop]);

  // Close drawer handler - collapses the drawer
  const handleDrawerClose = useCallback(() => {
    if (isDesktop) {
      setMiniDrawer(true);
    } else {
      setMobileOpen(false);
    }
  }, [isDesktop]);

  // Toggle drawer handler - toggles between expanded and collapsed states
  const handleDrawerToggle = useCallback(() => {
    if (isDesktop) {
      setMiniDrawer(prev => !prev);
    } else {
      setMobileOpen(prev => !prev);
    }
  }, [isDesktop]);

  return {
    isDesktop,
    mobileOpen,
    miniDrawer,
    handleDrawerOpen,
    handleDrawerClose,
    handleDrawerToggle,
    setMobileOpen,
    setMiniDrawer
  };
};

export default useDrawer;
